"use client";

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import {
  isValidEmail,
  subscribeNewsletter,
  subscribeNewsletterVerified,
  type NewsletterBrand,
} from "@/lib/newsletter";
import { useInvisibleRecaptcha } from "@/lib/useInvisibleRecaptcha";
import styles from "./newsletter-form.module.css";

interface NewsletterFormProps {
  brand: NewsletterBrand;
  brandId: string;
  /** Class on the rendered `<form>` (preserves each footer's theme styling). */
  className?: string;
  /**
   * Inline style for the wrapper that holds the form AND the status toast.
   * Pass the same `max-width` / `margin` rules the underlying `<form>` uses
   * so the toast lane lines up directly under the form on wide screens.
   * Example for main footer: `{ maxWidth: 660, marginLeft: "auto" }`.
   */
  wrapStyle?: CSSProperties;
  /** Field name the email input MUST use — defaults to "email". */
  emailFieldName?: string;
  /** Field name for an optional first-name input — defaults to "firstName".
   *  Only sent when an input with this name is present in `children`. */
  firstNameFieldName?: string;
  /** Field name for an optional last-name input — defaults to "lastName".
   *  Only sent when an input with this name is present in `children`. */
  lastNameFieldName?: string;
  /**
   * Protect this form with invisible reCAPTCHA, submitting via
   * `/api/newsletter` so the token is verified server-side.
   *
   * Opt-in rather than always-on: this component renders in every footer, and
   * mounting the widget also shows Google's floating badge on every page that
   * footer appears in. Footers that don't opt in keep posting directly and
   * load no reCAPTCHA script at all.
   */
  recaptcha?: boolean;
  /** The input + button markup. Must include an input with the email name. */
  children: ReactNode;
  /** Override the default success copy. */
  successMessage?: string;
  /** Override the default validation copy. */
  invalidEmailMessage?: string;
  /** Copy shown when a present first-name field is left empty. */
  requiredFirstNameMessage?: string;
  /** Copy shown when a present last-name field is left empty. */
  requiredLastNameMessage?: string;
}

type Status =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "success"; message: string }
  | { kind: "error"; message: string };

const AUTO_DISMISS_MS = 5000;

/**
 * Reusable newsletter subscribe form. Owns submit + feedback state; lets
 * the parent supply its own visual markup as children (so each footer keeps
 * its own theme). After success or error, a compact toast slides in below
 * the form and auto-dismisses.
 *
 * Consumers must include an `<input type="email" name="email">` inside
 * `children` (the name is configurable via `emailFieldName`).
 */
export default function NewsletterForm({
  brand,
  brandId,
  className,
  wrapStyle,
  emailFieldName = "email",
  firstNameFieldName = "firstName",
  lastNameFieldName = "lastName",
  recaptcha = false,
  children,
  successMessage = "You're subscribed — welcome aboard.",
  invalidEmailMessage = "Please enter a valid email address.",
  requiredFirstNameMessage = "Please enter your first name.",
  requiredLastNameMessage = "Please enter your last name.",
}: NewsletterFormProps) {
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const formRef = useRef<HTMLFormElement>(null);
  // Safe to call unconditionally: the hook only loads Google's script once its
  // container is actually mounted, which happens only when `recaptcha` is set.
  const { containerRef: recaptchaRef, getToken } = useInvisibleRecaptcha();

  // Auto-dismiss success/error toasts.
  useEffect(() => {
    if (status.kind !== "success" && status.kind !== "error") return;
    const t = window.setTimeout(
      () => setStatus({ kind: "idle" }),
      AUTO_DISMISS_MS,
    );
    return () => window.clearTimeout(t);
  }, [status]);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status.kind === "submitting") return;

    const form = e.currentTarget;
    const formData = new FormData(form);
    const email = String(formData.get(emailFieldName) ?? "").trim();
    const firstName = String(formData.get(firstNameFieldName) ?? "").trim();
    const lastName = String(formData.get(lastNameFieldName) ?? "").trim();

    // The name inputs are optional markup — only some footers render them.
    // When a footer DOES include them, treat them as required.
    const hasFirstNameField =
      form.elements.namedItem(firstNameFieldName) != null;
    const hasLastNameField = form.elements.namedItem(lastNameFieldName) != null;

    if (hasFirstNameField && !firstName) {
      setStatus({ kind: "error", message: requiredFirstNameMessage });
      return;
    }
    if (hasLastNameField && !lastName) {
      setStatus({ kind: "error", message: requiredLastNameMessage });
      return;
    }
    if (!isValidEmail(email)) {
      setStatus({ kind: "error", message: invalidEmailMessage });
      return;
    }
    if (!brandId) {
      setStatus({
        kind: "error",
        message: "Subscription is temporarily unavailable.",
      });
      return;
    }

    setStatus({ kind: "submitting" });
    try {
      if (recaptcha) {
        // Resolves "" when reCAPTCHA is unavailable (script blocked, no site
        // key); the route handler decides what that means, so the rule lives
        // in one place for every protected form.
        const recaptchaToken = await getToken();
        await subscribeNewsletterVerified({
          brand,
          brandId,
          email,
          firstName,
          lastName,
          recaptchaToken,
        });
      } else {
        await subscribeNewsletter({
          brand,
          brandId,
          email,
          firstName,
          lastName,
        });
      }
      setStatus({ kind: "success", message: successMessage });
      formRef.current?.reset();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Couldn't subscribe right now. Please try again.";
      setStatus({ kind: "error", message });
    }
  }

  const submitting = status.kind === "submitting";
  const toast =
    status.kind === "success" || status.kind === "error" ? status : null;

  return (
    <div className={styles.wrap} style={wrapStyle}>
      <form
        ref={formRef}
        className={className}
        onSubmit={handleSubmit}
        noValidate
      >
        {/* `fieldset[disabled]` cascades to all interactive descendants
            without forcing callers to pass disabled to their own buttons. */}
        <fieldset className={styles.fieldset} disabled={submitting}>
          {children}
        </fieldset>

        {/* Invisible reCAPTCHA mounts here when enabled — no UI of its own
            beyond the floating badge. Kept outside the fieldset so it is never
            affected by the disabled-while-submitting cascade. */}
        {recaptcha ? <div ref={recaptchaRef} /> : null}
      </form>

      {/* Compact, modern status toast — slides in beneath the form, fades
          out on dismiss. Polite live-region so AT users get the result. */}
      <div className={styles.toastLane} aria-live="polite" aria-atomic="true">
        {toast ? (
          <div
            className={`${styles.toast} ${
              toast.kind === "success" ? styles.toastSuccess : styles.toastError
            }`}
            role={toast.kind === "error" ? "alert" : "status"}
          >
            <span className={styles.toastIcon} aria-hidden="true">
              <i
                className={
                  toast.kind === "success"
                    ? "fa-solid fa-circle-check"
                    : "fa-solid fa-circle-exclamation"
                }
              />
            </span>
            <span className={styles.toastMessage}>{toast.message}</span>
            <button
              type="button"
              className={styles.toastClose}
              aria-label="Dismiss notification"
              onClick={() => setStatus({ kind: "idle" })}
            >
              <i className="fa-solid fa-xmark" aria-hidden="true" />
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
